import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, CheckCircle2, AlertCircle, Trash2, Edit3 } from "lucide-react";

const fieldCls = "w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3355]";

export default function VendorsAdminPage() {
    const [rows, setRows] = useState([]);
    const [cats, setCats] = useState([]);
    const [sites, setSites] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [spendDialog, setSpendDialog] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const [v, c, s] = await Promise.all([api.get("/vendors"), api.get("/expense-categories"), api.get("/sites")]);
            setRows(v.data); setCats(c.data); setSites(s.data);
        } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function del(id) {
        if (!window.confirm("Delete vendor?")) return;
        try { await api.delete(`/vendors/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(apiError(e)); }
    }
    async function openSpend(v) {
        try {
            const { data } = await api.get(`/vendors/${v.id}/spend`);
            setSpendDialog(data);
        } catch (e) { toast.error(apiError(e)); }
    }

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8">
            <div className="flex items-end justify-between">
                <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500">Admin</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Vendor master</h1>
                </div>
                <button onClick={() => setEditing({})} className="rounded-md bg-[#E02B2B] text-white px-3 py-2 text-sm font-medium" data-testid="vendor-add">
                    <Plus className="inline w-4 h-4 mr-1" />Add vendor
                </button>
            </div>
            <div className="mt-6 rounded-xl bg-white border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-[#F4F6F9] text-xs uppercase tracking-wider text-gray-500">
                            <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Categories</th><th className="px-3 py-2 text-left">GST</th><th className="px-3 py-2 text-left">Rate notes</th><th className="px-3 py-2">Verified</th><th className="px-3 py-2"></th></tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? <tr><td colSpan={6} className="p-6 text-center text-gray-400">Loading…</td></tr> :
                                rows.map((v) => (
                                    <tr key={v.id} data-testid={`vendor-row-${v.name}`}>
                                        <td className="px-3 py-2 font-medium text-[#1E3355]">
                                            <button onClick={() => openSpend(v)} className="hover:underline" data-testid={`vendor-open-${v.name}`}>{v.name}</button>
                                        </td>
                                        <td className="px-3 py-2 text-xs">{(v.categories || []).join(", ")}</td>
                                        <td className="px-3 py-2 text-xs">{v.gst_number || "—"}</td>
                                        <td className="px-3 py-2 text-xs">{v.rate_notes || "—"}</td>
                                        <td className="px-3 py-2 text-center">{v.verified ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" /> : <AlertCircle className="w-4 h-4 text-amber-600 inline" />}</td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <button onClick={() => setEditing(v)} className="text-gray-500 hover:text-[#1E3355] mr-2" data-testid={`vendor-edit-${v.name}`}><Edit3 className="w-4 h-4 inline" /></button>
                                            <button onClick={() => del(v.id)} className="text-gray-500 hover:text-red-600" data-testid={`vendor-del-${v.name}`}><Trash2 className="w-4 h-4 inline" /></button>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {editing && <VendorForm vendor={editing.id ? editing : null} categories={cats} sites={sites} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
            {spendDialog && <VendorSpendModal data={spendDialog} onClose={() => setSpendDialog(null)} />}
        </div>
    );
}

function VendorForm({ vendor, categories, sites, onClose, onSaved }) {
    const isEdit = !!vendor;
    const [form, setForm] = useState({
        name: vendor?.name || "",
        categories: vendor?.categories || [],
        gst_number: vendor?.gst_number || "",
        contact: vendor?.contact || "",
        sites_served: vendor?.sites_served || [],
        rate_notes: vendor?.rate_notes || "",
        verified: vendor?.verified ?? true,
    });
    const [saving, setSaving] = useState(false);
    async function save() {
        setSaving(true);
        try {
            if (isEdit) await api.patch(`/vendors/${vendor.id}`, form);
            else await api.post("/vendors", form);
            toast.success("Saved");
            onSaved();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    }
    function toggle(list, val) {
        return list.includes(val) ? list.filter((x) => x !== val) : [...list, val];
    }
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="vendor-form">
            <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 border-b flex items-center justify-between">
                    <div className="font-bold text-[#1E3355]">{isEdit ? "Edit vendor" : "New vendor"}</div>
                    <button onClick={onClose}><X className="w-4 h-4" /></button>
                </div>
                <div className="p-4 space-y-3">
                    <div><div className="text-xs text-gray-500 mb-1">Name</div><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} data-testid="vf-name" /></div>
                    <div>
                        <div className="text-xs text-gray-500 mb-1">Categories served</div>
                        <div className="flex flex-wrap gap-1.5">
                            {categories.map((c) => (
                                <button key={c.id} onClick={() => setForm({ ...form, categories: toggle(form.categories, c.name) })}
                                    className={`px-2 py-1 rounded-md text-xs border ${form.categories.includes(c.name) ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-700 border-gray-200"}`}
                                    data-testid={`vf-cat-${c.name}`}>
                                    {c.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><div className="text-xs text-gray-500 mb-1">GST number</div><input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} className={fieldCls} data-testid="vf-gst" /></div>
                        <div><div className="text-xs text-gray-500 mb-1">Contact</div><input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className={fieldCls} data-testid="vf-contact" /></div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 mb-1">Sites served</div>
                        <div className="flex flex-wrap gap-1.5">
                            {sites.map((s) => (
                                <button key={s.id} onClick={() => setForm({ ...form, sites_served: toggle(form.sites_served, s.id) })}
                                    className={`px-2 py-1 rounded-md text-xs border ${form.sites_served.includes(s.id) ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-700 border-gray-200"}`}
                                    data-testid={`vf-site-${s.name}`}>
                                    {s.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div><div className="text-xs text-gray-500 mb-1">Rate notes</div><textarea value={form.rate_notes} onChange={(e) => setForm({ ...form, rate_notes: e.target.value })} rows={2} className={fieldCls} data-testid="vf-notes" /></div>
                    <label className="inline-flex items-center gap-2 text-sm text-emerald-700"><input type="checkbox" checked={form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} data-testid="vf-verified" /> Verified</label>
                </div>
                <div className="p-4 border-t flex justify-end gap-2">
                    <button onClick={onClose} className="rounded-md border border-gray-200 px-3 py-2 text-sm">Cancel</button>
                    <button onClick={save} disabled={saving || !form.name.trim()} className="rounded-md bg-[#1E3355] text-white px-4 py-2 text-sm font-medium disabled:opacity-60" data-testid="vf-save">{saving ? "Saving…" : "Save"}</button>
                </div>
            </div>
        </div>
    );
}

function VendorSpendModal({ data, onClose }) {
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="vendor-spend-modal">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 border-b flex items-center justify-between">
                    <div className="font-bold text-[#1E3355]">{data.vendor.name} — spend</div>
                    <button onClick={onClose}><X className="w-4 h-4" /></button>
                </div>
                <div className="p-4">
                    <div className="text-3xl font-bold text-[#1E3355]" data-testid="vs-total">₹ {Number(data.total).toLocaleString("en-IN")}</div>
                    <div className="text-xs text-gray-500 mt-1">across {data.expenses.length} entries</div>
                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                        {Object.entries(data.by_site).map(([sid, v]) => (
                            <div key={sid} className="rounded-lg bg-[#F4F6F9] p-3" data-testid={`vs-site-${v.site_name}`}>
                                <div className="text-xs text-gray-500">{v.site_name}</div>
                                <div className="font-bold text-[#1E3355]">₹ {Number(v.total).toLocaleString("en-IN")} <span className="text-xs text-gray-400 font-normal">· {v.count} entries</span></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
